/**
 * PhoneInput
 * ----------
 * Country-code-aware phone input. Mandatory country selection (no plain
 * national-only numbers). Stores the value in E.164 format
 * (e.g. ``+919844139333``) so the backend never has to guess.
 *
 * Props:
 *   value         — current E.164 string ("" or undefined for empty)
 *   onChange      — receives the new E.164 string (or "" if cleared)
 *   onValidityChange — optional, fires (true/false) when the input becomes valid/invalid
 *   defaultCountry — ISO2 fallback when value is empty (default "IN")
 *   required      — for native form integration
 *   placeholder, autoFocus, disabled, testId, label, name — standard
 *   error         — string error message to render below
 *
 * Behaviour:
 *   * The dropdown shows flag + name + dial code, with type-ahead search by
 *     name, ISO2 (e.g. "IN") or dial code ("91").
 *   * Pasting a number that begins with "+" automatically detects + applies
 *     the country.
 *   * `onValidityChange` uses `libphonenumber-js` to determine validity.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { parsePhoneNumberFromString, getCountryCallingCode } from "libphonenumber-js/min";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";
import { COUNTRIES, DEFAULT_COUNTRY, findByCode, findByDial } from "@/lib/countries";

function splitE164(value) {
  if (!value) return { country: DEFAULT_COUNTRY, national: "" };
  // Strip non-essentials.
  let v = String(value).trim();
  if (!v.startsWith("+")) v = `+${v}`;
  const parsed = parsePhoneNumberFromString(v);
  if (parsed && parsed.country) {
    return {
      country: findByCode(parsed.country),
      national: parsed.nationalNumber,
    };
  }
  // Fall back to longest-prefix match on dial code.
  const digits = v.slice(1).replace(/\D/g, "");
  for (let len = 4; len >= 1; len -= 1) {
    const match = findByDial(digits.slice(0, len));
    if (match) {
      return { country: match, national: digits.slice(len) };
    }
  }
  return { country: DEFAULT_COUNTRY, national: digits };
}

function buildE164(country, national) {
  const digits = (national || "").replace(/\D/g, "");
  if (!digits) return "";
  return `+${country.dial}${digits}`;
}

export default function PhoneInput({
  value = "",
  onChange,
  onValidityChange,
  defaultCountry = "IN",
  required = false,
  disabled = false,
  placeholder = "Phone number",
  autoFocus = false,
  testId = "phone-input",
  label,
  name,
  error,
  className = "",
}) {
  const initial = useMemo(() => {
    if (value) return splitE164(value);
    return { country: findByCode(defaultCountry), national: "" };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [country, setCountry] = useState(initial.country);
  const [national, setNational] = useState(initial.national);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [touched, setTouched] = useState(false);
  const wrapperRef = useRef(null);

  // Keep internal state in sync if the parent rewrites `value` from outside.
  useEffect(() => {
    const next = value ? splitE164(value) : { country, national: "" };
    if (next.country.code !== country.code) setCountry(next.country);
    if (next.national !== national) setNational(next.national);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Tell the parent every time we have a new E.164 or it goes blank.
  useEffect(() => {
    const e164 = buildE164(country, national);
    if (e164 !== value) onChange?.(e164);
    // Validity:
    const parsed = e164 ? parsePhoneNumberFromString(e164) : null;
    onValidityChange?.(!!parsed && parsed.isValid());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, national]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleNationalChange = (raw) => {
    let v = raw;
    // If user pastes/types a value starting with "+", auto-detect country.
    if (v.startsWith("+")) {
      const parsed = parsePhoneNumberFromString(v);
      if (parsed && parsed.country) {
        setCountry(findByCode(parsed.country));
        setNational(parsed.nationalNumber);
        return;
      }
    }
    setNational(v.replace(/[^\d]/g, ""));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.includes(q.replace(/^\+/, "")) ||
        (c.aliases || []).some((a) => a.includes(q))
    );
  }, [search]);

  const e164 = buildE164(country, national);
  const parsedNow = e164 ? parsePhoneNumberFromString(e164) : null;
  const isInvalid = touched && national.length > 0 && (!parsedNow || !parsedNow.isValid());

  return (
    <div className={`w-full ${className}`} ref={wrapperRef} data-testid={testId}>
      {label && (
        <label className="block text-sm font-medium text-ink mb-1.5">
          {label}{required ? <span className="text-terracotta"> *</span> : null}
        </label>
      )}
      <div
        className={`flex items-stretch rounded-xl border bg-white overflow-hidden h-12 ${
          isInvalid || error ? "border-red-400" : "border-border focus-within:ring-2 focus-within:ring-terracotta focus-within:border-transparent"
        }`}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className="px-3 inline-flex items-center gap-1.5 text-sm font-medium bg-paper-alt hover:bg-paper border-r border-border min-w-[112px] justify-between disabled:opacity-60"
          data-testid={`${testId}-country-btn`}
          aria-label="Country code"
        >
          <span className="text-lg leading-none">{country.flag}</span>
          <span className="font-mono text-xs">+{country.dial}</span>
          <CaretDown weight="bold" size={12} />
        </button>
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          name={name}
          value={national}
          onChange={(e) => handleNationalChange(e.target.value)}
          onBlur={() => setTouched(true)}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="flex-1 px-3 text-sm outline-none disabled:opacity-60"
          data-testid={`${testId}-national`}
        />
      </div>
      {open && (
        <div
          className="relative z-30"
          data-testid={`${testId}-dropdown`}
        >
          <div className="absolute left-0 right-0 mt-2 w-full bg-white border border-border rounded-2xl shadow-floating overflow-hidden">
            <div className="p-2 border-b border-border bg-paper-alt flex items-center gap-2">
              <MagnifyingGlass size={14} weight="bold" className="text-ink-soft" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                placeholder="Search by country, ISO code, or dial code"
                className="flex-1 bg-transparent text-sm outline-none"
                data-testid={`${testId}-search`}
              />
              {search && (
                <button onClick={() => setSearch("")} className="text-ink-soft" type="button">
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
            <ul className="max-h-72 overflow-y-auto" role="listbox">
              {filtered.length === 0 && (
                <li className="px-3 py-4 text-xs text-ink-soft text-center">No matches</li>
              )}
              {filtered.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => {
                      setCountry(c);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-paper-alt ${
                      c.code === country.code ? "bg-mustard-soft font-semibold" : ""
                    }`}
                    data-testid={`${testId}-opt-${c.code}`}
                    role="option"
                    aria-selected={c.code === country.code}
                  >
                    <span className="text-lg leading-none">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="font-mono text-xs text-ink-soft">+{c.dial}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {(isInvalid || error) && (
        <div className="text-xs text-red-600 mt-1" data-testid={`${testId}-error`}>
          {error || "Enter a valid phone number for the selected country."}
        </div>
      )}
    </div>
  );
}
